import { Component, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EventInput } from '@fullcalendar/core';

/**
 * An event temporarily parked on the shelf, waiting to be dropped on another day.
 */
export interface ParkedEvent {
  event: EventInput;
  sourceCollaboratoreId: string;
}

/**
 * The drop payload emitted when a parked card starts being dragged again.
 */
export interface ParkedEventDragStart {
  dragEvent: DragEvent;
  parked: ParkedEvent;
}

/**
 * Presentational "parking shelf" for the resource-day grid: a drop target
 * where the user can temporarily park a dragged event (e.g. to move it to a
 * different day) and pick it back up later. Owns only its own hover-visual
 * state — the parked list and the actual drag/drop business logic stay in
 * `ResourceDayCalendarComponent`, which owns the cross-column drag session.
 */
@Component({
  selector: 'siat-parking-shelf',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './parking-shelf.component.html',
  styleUrl: './parking-shelf.component.scss',
})
export class ParkingShelfComponent {
  /**
   * The events currently parked on the shelf.
   */
  readonly parkedEvents = input<ParkedEvent[]>([]);

  /**
   * Emitted when an event is dropped onto the shelf.
   */
  readonly dropped = output<DragEvent>();

  /**
   * Emitted when a parked card starts being dragged again.
   */
  readonly parkedDragStart = output<ParkedEventDragStart>();

  /**
   * Emitted when a parked card's remove button is clicked.
   */
  readonly removeParked = output<string | number>();

  /**
   * True while a drag is hovering over the shelf.
   */
  protected readonly isDragOver = signal(false);

  /**
   * Sets the hover feedback when the drag enters the shelf.
   */
  protected onDragOver(dragEvent: DragEvent): void {
    dragEvent.preventDefault();
    this.isDragOver.set(true);
  }

  /**
   * Clears the hover feedback when the drag leaves the shelf.
   */
  protected onDragLeave(): void {
    this.isDragOver.set(false);
  }

  /**
   * Clears the hover feedback and forwards the drop to the parent.
   */
  protected onDrop(dragEvent: DragEvent): void {
    dragEvent.preventDefault();
    this.isDragOver.set(false);
    this.dropped.emit(dragEvent);
  }

  /**
   * Forwards a parked card's drag-start to the parent.
   */
  protected onCardDragStart(dragEvent: DragEvent, parked: ParkedEvent): void {
    this.parkedDragStart.emit({ dragEvent, parked });
  }
}
